/*
|--------------------------------------------------------------------------
| Utility: variantUtils.js
| Author: Sayed Ahmed
| Description: Provides helper functions to manage product variant logic,
|              including generating combinations, SKUs, and comparing variants.
|--------------------------------------------------------------------------
*/

// Generate all possible combinations of attribute values
export function generateCombinations(attributeValuesArray) {
  return attributeValuesArray.reduce((acc, curr) => {
    if (acc.length === 0) return curr.map((v) => [v]);
    return acc.flatMap((combo) => curr.map((v) => [...combo, v]));
  }, []);
}

// Create a map of value.id to attribute_id
export function mapValueToAttribute(attributeObject) {
  const map = {};
  Object.entries(attributeObject).forEach(([attrId, attr]) => {
    attr.values.forEach((val) => {
      map[val.id] = attrId;
    });
  });
  return map;
}

// Generate a new product variant object based on the selected values
export function generateVariantObject(combo, valueToAttributeMap, attributes, baseSku, basePrice, baseStock, baseDiscount = 0, baseDiscountType = "fixed") {
  const displayValues = combo.map((v) => {
    const attrId = Number(valueToAttributeMap[v.id]);
    return {
      id: v.id,
      attribute_id: attrId,
      value: v.value,
      hex: v.hex,
      pivot: {
        product_variant_id: null,
        attribute_value_id: v.id,
      },
      attribute: {
        id: attrId,
        name: attributes[attrId]?.name || "",
      },
    };
  });

  return {
    sku: baseSku,
    price: basePrice,
    discount: baseDiscount,
    discount_type: baseDiscountType,
    stock: baseStock,
    images: [],
    attribute_values_display: displayValues,
    attribute_values: combo.map((v) => v.id),
  };
}

// Check if a new combination already exists to prevent duplication
export function isDuplicateVariant(combo, existingVariant, valueToAttributeMap) {
  if (!existingVariant || existingVariant.attribute_values_display.length !== combo.length) return false;

  return combo.every((comboValue) =>
    existingVariant.attribute_values_display.some(
      (v) => v.id === comboValue.id && v.attribute_id === Number(valueToAttributeMap[comboValue.id])
    )
  );
}

// Auto-generate SKU based on selected attribute values
export function generateSku(baseSkuCode, attributeValuesDisplay = []) {
  if (!Array.isArray(attributeValuesDisplay)) return baseSkuCode;
  const skuSuffix = attributeValuesDisplay.map((v) => v.value).join("-");
  return `${baseSkuCode}-${skuSuffix}`;
}

// Get the value of a specific attribute from attribute_values_display
export function getAttributeValueFromDisplay(variant, attributeId) {
  if (!variant || !Array.isArray(variant.attribute_values_display)) return null;

  return variant.attribute_values_display.find(
    (v) => Number(v.attribute_id) === Number(attributeId)
  );
}

// Check if a SKU is auto-generated (matches the pattern)
export function isAutoGeneratedSku(sku, baseSku) {
  if (!sku || !baseSku) return false;
  return sku.startsWith(baseSku + "-");
}

// Generate a clean SKU without special characters
export function cleanSkuString(str) {
  return str.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase();
}

// Calculate final price after discount
export function calculateFinalPrice(price, discount, discountType) {
  if (!discount || discount <= 0) return price;
  
  if (discountType === 'percentage') {
    return price - (price * discount / 100);
  } else {
    return Math.max(0, price - discount);
  }
}

// Format price with currency
export function formatPrice(price, currency = '$') {
  return `${currency}${parseFloat(price).toFixed(2)}`;
}

// Calculate discount percentage
export function calculateDiscountPercentage(originalPrice, finalPrice) {
  if (!originalPrice || originalPrice <= 0) return 0;
  return Math.round(((originalPrice - finalPrice) / originalPrice) * 100);
}

// Get discount display text
export function getDiscountDisplayText(discount, discountType) {
  if (!discount || discount <= 0) return '';
  
  if (discountType === 'percentage') {
    return `${discount}% OFF`;
  } else {
    return `$${discount} OFF`;
  }
}